import { z } from "zod/v4";

import {
  AgenticLogicIngressRequestDescriptions,
  AgenticLogicRequestDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import { AgenticLogicSurfaceSchema } from "../../../dtos/agentic-logic/surface";
import { AgenticLogicCartDataSchema } from "./cart";
import { AgenticLogicCustomerDataSchema } from "./customer";
import { AgenticLogicOrderDataSchema } from "./order";
import { AgenticLogicPageProductSchema } from "./page-product";
import { AgenticLogicShippingContextSchema } from "./shipping";

/**
 * The market a scenario runs in when it names none.
 *
 * Exists exactly once so the non-serving lanes cannot each invent their own
 * literal: {@link AgenticLogicIngressRequestSchema} leaves `country` optional,
 * and the single stamper (`buildAgenticLogicScenarioInvocation`) resolves it
 * to this. Serving never reads it — there the caller's country is a fact.
 */
export const AGENTIC_LOGIC_DEFAULT_COUNTRY = "US";

/**
 * The caller-authored half of a request: everything a rule may read EXCEPT the
 * clock. The nullable context blocks (`cart`/`order`/`pageProduct`/`customer`)
 * are required keys with null values — never omitted keys — so a missing signal
 * is always an explicit statement, not an encoding ambiguity.
 *
 * This is what an HTTP evaluate/serve body carries and what a publish-gate
 * validation scenario stores. It exists as its own schema because those callers
 * must NOT supply `evaluatedAt`: the instant a rule is judged at belongs to the
 * platform, and a client-settable clock would be both forgeable and a second
 * source of truth for "now". See {@link AgenticLogicRequestSchema}.
 */
export const AgenticLogicIngressRequestSchema = z
  .object({
    surface: AgenticLogicSurfaceSchema.describe(
      AgenticLogicIngressRequestDescriptions.surface
    ),
    placementId: z
      .string()
      .describe(AgenticLogicIngressRequestDescriptions.placementId),
    cart: AgenticLogicCartDataSchema.nullable().describe(
      AgenticLogicIngressRequestDescriptions.cart
    ),
    order: AgenticLogicOrderDataSchema.nullable().describe(
      AgenticLogicIngressRequestDescriptions.order
    ),
    pageProduct: AgenticLogicPageProductSchema.nullable().describe(
      AgenticLogicIngressRequestDescriptions.pageProduct
    ),
    customer: AgenticLogicCustomerDataSchema.nullable().describe(
      AgenticLogicIngressRequestDescriptions.customer
    ),
    shipping: AgenticLogicShippingContextSchema.describe(
      AgenticLogicIngressRequestDescriptions.shipping
    ),
    // `.nullish()`, deliberately NOT `.default()`. Stored scenarios are JSON on
    // a draft row: nullish accepts both a missing key and an explicit null, so
    // every scenario written before this field existed parses unchanged. A
    // default would also make `country` REQUIRED on the inferred output type of
    // every ingress-shaped literal in the repo for no correctness gain — the
    // stamper guarantees the value at the only place it is read. That
    // asymmetry (optional at ingress, required rule-facing) is the point; see
    // {@link AgenticLogicRequestSchema}.
    //
    // The regex mirrors `ServeAgenticLogicRuleRequestBodySchema.country` so
    // ingress and serving agree on what a country IS, and a malformed value is
    // refused here rather than opaquely by Shopify's `@inContext`.
    country: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .nullish()
      .describe(AgenticLogicIngressRequestDescriptions.country),
    cartFingerprint: z
      .string()
      .optional()
      .describe(AgenticLogicIngressRequestDescriptions.cartFingerprint),
  })
  .describe(AgenticLogicIngressRequestDescriptions._schema);

export type AgenticLogicIngressRequest = z.infer<
  typeof AgenticLogicIngressRequestSchema
>;

/**
 * Everything a rule may read for one evaluation — the ingress request plus the
 * platform's pinned clock and pinned market.
 *
 * `evaluatedAt` is stamped exactly once per invocation and never by the caller:
 * the broker stamps it on every serving, preview, API and evaluate ingress, and
 * the publish gates stamp it once per job. Pinned, not live — that is what keeps
 * the stable-output gate satisfiable, since it invokes one scenario twice and
 * demands deeply-equal envelopes. A rule reading `evaluatedAt` twice, or a
 * scenario run twice, gets the same value.
 *
 * It is also the ONLY sanctioned "now" in rule code. `Date.now()` is refused
 * statically at save time and again at runtime by the bundle prelude, so
 * time-dependent logic that used to be inexpressible — replenishment windows,
 * recency decay, "ordered N days ago" — reads this instead.
 *
 * Note this must be a schema field rather than something the broker merely
 * spreads on: `AgenticLogicRuleLambdaInputSchema` is `.strict()`, and Zod strips
 * unknown keys on the non-strict request nested inside it. An unschema'd stamp
 * would be silently dropped before it ever reached a published rule.
 *
 * `country` is the second member of that family and is stamped the same way:
 * optional at ingress, REQUIRED here, and written by exactly two producers —
 * the broker on every serving path (from the caller's own country) and
 * `buildAgenticLogicScenarioInvocation` for every non-serving lane (preview,
 * publish gates, chain cost, required tests), defaulting to
 * {@link AGENTIC_LOGIC_DEFAULT_COUNTRY} when the scenario names none. Every
 * catalog read the SDK makes executes under this value via `@inContext`, so a
 * lane that pinned its own would be judging the rule in a market the rule was
 * never written for. Being required here is also what makes `request.country`
 * trustworthy for a rule to READ: it can now see its own market instead of
 * inferring it from a product's currency.
 */
export const AgenticLogicRequestSchema =
  AgenticLogicIngressRequestSchema.extend({
    country: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .describe(AgenticLogicRequestDescriptions.country),
    evaluatedAt: z
      .number()
      .int()
      .positive()
      .describe(AgenticLogicRequestDescriptions.evaluatedAt),
  }).describe(AgenticLogicRequestDescriptions._schema);

export type AgenticLogicRequest = z.infer<typeof AgenticLogicRequestSchema>;

assertDescriptions<
  typeof AgenticLogicIngressRequestDescriptions,
  AgenticLogicIngressRequest
>();
assertDescriptions<
  typeof AgenticLogicRequestDescriptions,
  AgenticLogicRequest
>();
